import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from './common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import './App.css';
import StopLossCalculator from './StopLossCalculator';
import StrikePriceCalculator from './StrikePriceCalculator';

const { 
  FiArrowLeft, FiCheck, FiX, FiTrendingUp, FiShield, 
  FiTarget, FiActivity, FiLayers, FiSettings, FiBarChart2,
  FiMessageSquare, FiSend, FiInfo, FiZap, FiExternalLink,
  FiUser, FiCpu, FiMoreHorizontal, FiGrid, FiCalculator
} = FiIcons;

const LOGO_URL = 'https://i.postimg.cc/ZndHykWJ/LOGO-NEW-RW-2027.png';

const WIZARD_CONFIG = {
  'Low Risk': {
    Buying: [
      { 
        id: 'A', 
        title: 'Step A: Angle', 
        question: 'Is the angle line ( Green in color) making more than 30 Degree with reference to the yellow line and pointing upwards ?',
        icon: FiActivity,
        detail: 'Upward momentum is required for a bullish setup. A shallow angle might indicate a weak trend.',
        image: 'https://i.postimg.cc/FRhmf12D/upangle.png'
      },
      { 
        id: 'B', 
        title: 'Step B: Breakout', 
        question: 'is green candle shifted above breakout level shown in blue color ?',
        icon: FiTarget,
        detail: 'Confirm that the price has cleared the recent high with conviction and volume.',
        image: 'https://i.postimg.cc/pX7rSQJZ/upbreak.png'
      },
      { 
        id: 'C', 
        title: 'Step C: Candles', 
        question: 'Are Green candles climbing up like stairs making a higher high pattern ?',
        icon: FiLayers,
        detail: 'Look for strong buying pressure in the candle shapes. Lower wicks are a positive sign.',
        image: 'https://i.postimg.cc/jddKhWVF/photo-collage-png-(2).png'
      },
      { 
        id: 'M', 
        title: 'Step M: Money Flow', 
        question: 'Is the Green Moneyflow line moving in an upward direction ?',
        icon: FiSettings,
        detail: 'Capital should be entering the asset for a sustainable pump. Green MFI indicates net inflow.',
        image: 'https://i.postimg.cc/fTpzTwFg/moneyflow-UP.png'
      },
      { 
        id: 'P', 
        title: 'Step P: Percentage', 
        question: 'Is the percentage table showing 78% or more percent and is Green in color ?',
        icon: FiBarChart2,
        detail: 'Ensure the potential gain justifies the stop-loss distance. High probability trades respect 1:2.',
        image: 'https://i.postimg.cc/63njRh53/uppercentage.png'
      }
    ],
    Selling: [
      { 
        id: 'A', 
        title: 'Step A: Angle', 
        question: 'Is the angle line ( Red in color) making more than 30 Degree with reference to the yellow line and pointing Downwards ?',
        icon: FiActivity,
        detail: 'Downward momentum is required for a bearish setup. Negative angles show persistent selling.',
        image: 'https://i.postimg.cc/tTF5c4JQ/downangle.png'
      },
      { 
        id: 'B', 
        title: 'Step B: Breakout', 
        question: 'Is the Orange Breakout happening in the downward direction as shown ?',
        icon: FiTarget,
        detail: 'Confirm that the price has dropped below the recent low with high relative volume.',
        image: 'https://i.postimg.cc/hG0SKVVD/downbreak.png'
      },
      { 
        id: 'C', 
        title: 'Step C: Candles', 
        question: 'Are Red candles climbing Down like stairs making a Lower Low pattern ?',
        icon: FiLayers,
        detail: 'Look for strong selling pressure in the candle shapes. Upper wicks indicate rejection.',
        image: 'https://i.postimg.cc/JzYbhBmV/photo-collage-png-(3).png'
      },
      { 
        id: 'M', 
        title: 'Step M: Money Flow', 
        question: 'Is the Red Moneyflow line moving in a Downward direction ?',
        icon: FiSettings,
        detail: 'Capital should be exiting the asset for a sustainable drop. Red MFI indicates net outflow.',
        image: 'https://i.postimg.cc/8kFP8JXD/moneyflow-Down.png'
      },
      { 
        id: 'P', 
        title: 'Step P: Percentage', 
        question: 'Is the percentage table showing 78 or more percent and is Red in color ?',
        icon: FiBarChart2,
        detail: 'Ensure the potential drop profit justifies the stop-loss. Don\'t chase overextended moves.',
        image: 'https://i.postimg.cc/KjcPz71W/Downpercentage.png'
      }
    ]
  },
  'High Risk': {
    Buying: [
      { 
        id: 'M', 
        title: 'Step M: Money Flow', 
        question: 'Is the Green Moneyflow line moving in a Upward direction ?',
        icon: FiSettings,
        detail: 'High risk trades require aggressive capital support to overcome resistance levels.',
        image: 'https://i.postimg.cc/K8cHVVmc/MFhigh-Buying.png'
      },
      { 
        id: 'A', 
        title: 'Step A: Angle', 
        question: 'Is the angle line making more than 30 Degree with reference to the zero line (Central line) and pointing Upwards ?',
        icon: FiActivity,
        detail: 'Aggressive setups often rely on steep momentum angles indicating a parabolic move.',
        image: 'https://i.postimg.cc/SQB4Bmvw/Upanglehigh.png'
      },
      { 
        id: 'C', 
        title: 'Step C: Candle', 
        question: 'Are Green candles climbing UP like stairs making a Higher High pattern ?',
        icon: FiLayers,
        detail: 'High volume confirmation is critical for aggressive entries to ensure followers are present.',
        image: 'https://i.postimg.cc/jddKhWVF/photo-collage-png-(2).png'
      },
      { 
        id: 'P', 
        title: 'Step P: Percentage', 
        question: 'Is the percentage table showing 78 or more percent and is Green in color ?',
        icon: FiBarChart2,
        detail: 'Ensure volatility doesn\'t exceed your stop-loss tolerance in high-risk environments.',
        image: 'https://i.postimg.cc/63njRh53/uppercentage.png'
      }
    ],
    Selling: [
      { 
        id: 'M', 
        title: 'Step M: Money Flow', 
        question: 'Is the Red Moneyflow line moving in a Downward direction ?',
        icon: FiSettings,
        detail: 'Selling pressure must be backed by heavy selling volume to confirm a trend reversal.',
        image: 'https://i.postimg.cc/mrk2b8Ny/MFhigh-Selling.png'
      },
      { 
        id: 'A', 
        title: 'Step A: Angle', 
        question: 'Is the angle line making more than 30 Degree with reference to the zero line (Central line) and pointing Downwards ?',
        icon: FiActivity,
        detail: 'Aggressive shorts benefit from steep negative slopes indicating panic selling.',
        image: 'https://i.postimg.cc/9Qy65T7p/Down-Angle-High.png'
      },
      { 
        id: 'C', 
        title: 'Step C: Candle', 
        question: 'Are Red candles climbing Down like stairs making a Lower Low pattern ?',
        icon: FiLayers,
        detail: 'High risk shorts need definitive structural breaks to avoid being trapped in fakeouts.',
        image: 'https://i.postimg.cc/JzYbhBmV/photo-collage-png-(3).png'
      },
      { 
        id: 'P', 
        title: 'Step P: Percentage', 
        question: 'Is the percentage table showing 78 or more percent and is Red in color ?',
        icon: FiBarChart2,
        detail: 'Double check your risk exposure on this high-risk sell. High risk means high probability of stop-out.',
        image: 'https://i.postimg.cc/KjcPz71W/Downpercentage.png'
      }
    ]
  }
};

const TypingIndicator = () => (
  <div className="flex space-x-1 p-2 bg-white/5 rounded-2xl rounded-tl-none w-fit border border-white/5">
    <motion.div
      className="w-1.5 h-1.5 bg-red-500 shadow-[0_0_8px_rgba(239,68,68,0.8)] rounded-full"
      animate={{ y: [0, -5, 0] }}
      transition={{ duration: 0.6, repeat: Infinity, delay: 0 }}
    />
    <motion.div
      className="w-1.5 h-1.5 bg-red-500 shadow-[0_0_8px_rgba(239,68,68,0.8)] rounded-full"
      animate={{ y: [0, -5, 0] }}
      transition={{ duration: 0.6, repeat: Infinity, delay: 0.2 }}
    />
    <motion.div
      className="w-1.5 h-1.5 bg-red-500 shadow-[0_0_8px_rgba(239,68,68,0.8)] rounded-full"
      animate={{ y: [0, -5, 0] }}
      transition={{ duration: 0.6, repeat: Infinity, delay: 0.4 }}
    />
  </div>
);

const Chatbot = ({ currentStepData, currentStep, isOpen, setIsOpen }) => {
  const [messages, setMessages] = useState([
    { role: 'bot', text: 'Hi! I am the Rocketalgo Assistant. How can I help you today?' }
  ]);
  const [input, setInput] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, isTyping]);

  const generateResponse = (text) => {
    const lowerInput = text.toLowerCase();
    
    // Priority 1: Critical & Specific Commands
    if (lowerInput.includes('help')) {
      return "Please contact us at +91-77-969-83-999 for assistance.";
    }

    // Priority 2: Explicit requests for explanation (Moved up to prevent 'hi' false positives)
    if (lowerInput.includes('detail') || lowerInput.includes('explain') || lowerInput.includes('meaning') || lowerInput.includes('what')) {
      if (currentStepData) {
        return `**${currentStepData.title}:** ${currentStepData.detail}`;
      } else {
        return "I can explain specific rules once you start the validation process.";
      }
    }
    
    // Priority 3: Context-specific guidance for main wizard steps
    if (!currentStepData) {
      if (currentStep === 1) return "Welcome! Please click 'Launch Guide' to start the setup wizard.";
      if (currentStep === 2) return "Please select a Trading Style: 'Low Risk' for conservative trades or 'High Risk' for aggressive ones.";
      if (currentStep === 3) return "Review the strategy pointers above. Once you're ready, click 'Let's Go'.";
      if (currentStep === 4) return "Are you planning to Buy (Bullish) or Sell (Bearish)? Select an action to proceed.";
    }

    // Priority 4: Greetings & Small Talk
    // Use regex to match whole word "hi" to avoid matching "this", "high", etc.
    if (/\bhi\b/.test(lowerInput) || lowerInput.includes('hello')) {
      return "Hello there! Ready to validate some trades?";
    }

    if (lowerInput.includes('thank')) {
      return "You're welcome! Good luck with your trade.";
    }

    // Fallback
    return "I'm not sure about that. Try asking for 'details' or 'help' with the current step.";
  };

  const handleSend = (text = input) => {
    if (!text.trim()) return;

    const userMsg = { role: 'user', text: text };
    setMessages(prev => [...prev, userMsg]);
    setInput('');
    setIsTyping(true);

    // Simulate network delay
    setTimeout(() => {
      const botResponse = generateResponse(text);
      setMessages(prev => [...prev, { role: 'bot', text: botResponse }]);
      setIsTyping(false);
    }, 1000);
  };

  const quickActions = [
    { label: "Explain this step", action: "Explain the details of this step" },
    { label: "What's next?", action: "What should I do next?" },
    { label: "Help", action: "I need help" }
  ];

  return (
    <div className="fixed bottom-6 right-6 z-[60]">
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 50, scale: 0.9, rotateX: 10 }}
            animate={{ opacity: 1, y: 0, scale: 1, rotateX: 0 }}
            exit={{ opacity: 0, y: 50, scale: 0.9, rotateX: 10 }}
            transition={{ type: "spring", damping: 25, stiffness: 300 }}
            className="chat-window mb-4 w-[85vw] md:w-80 h-[500px] max-h-[70vh] bg-black/95 border border-red-500/30 shadow-[0_0_30px_rgba(220,38,38,0.2)] rounded-3xl flex flex-col overflow-hidden backdrop-blur-xl"
          >
            {/* Header */}
            <div className="p-4 bg-gradient-to-r from-red-900 to-black flex items-center justify-between shadow-lg relative overflow-hidden border-b border-white/10">
              <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/carbon-fibre.png')] opacity-20"></div>
              <div className="flex items-center gap-3 relative z-10">
                <div className="w-8 h-8 bg-black/50 rounded-full flex items-center justify-center backdrop-blur-sm border border-red-500/50 shadow-[0_0_10px_rgba(239,68,68,0.4)]">
                  <SafeIcon icon={FiCpu} className="w-4 h-4 text-red-500" />
                </div>
                <div>
                  <h3 className="text-white font-black text-sm tracking-wide glow-text-red">RocketAssistant</h3>
                  <div className="flex items-center gap-1.5">
                    <span className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse shadow-[0_0_5px_rgba(34,197,94,0.8)]"></span>
                    <span className="text-[10px] text-white/90 font-medium">Online</span>
                  </div>
                </div>
              </div>
              <button 
                onClick={() => setIsOpen(false)} 
                className="text-white/80 hover:text-red-500 p-1 hover:bg-white/5 rounded-lg transition-all relative z-10"
              >
                <SafeIcon icon={FiX} className="w-5 h-5" />
              </button>
            </div>

            {/* Messages Area */}
            <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-4 custom-scrollbar bg-gradient-to-b from-transparent to-black/20">
              <div className="text-center py-4">
                <p className="text-[10px] text-white/30 uppercase tracking-widest font-bold">Today</p>
              </div>
              
              {messages.map((m, idx) => (
                <motion.div 
                  key={idx} 
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  <div className={`flex items-end gap-2 max-w-[85%] ${m.role === 'user' ? 'flex-row-reverse' : 'flex-row'}`}>
                    <div className={`w-6 h-6 rounded-full flex-shrink-0 flex items-center justify-center text-[10px] border ${m.role === 'user' ? 'bg-zinc-800 border-white/10 text-white' : 'bg-red-500/10 border-red-500/50 text-red-500 shadow-[0_0_10px_rgba(239,68,68,0.2)]'}`}>
                      <SafeIcon icon={m.role === 'user' ? FiUser : FiCpu} className="w-3 h-3" />
                    </div>
                    <div className={`p-3 rounded-2xl text-xs leading-relaxed shadow-sm ${
                      m.role === 'user' 
                        ? 'bg-zinc-900/80 text-white rounded-br-none border border-white/10' 
                        : 'bg-red-500/5 text-white/90 rounded-bl-none border border-red-500/20 shadow-[0_0_10px_rgba(239,68,68,0.05)]'
                    }`}>
                      {m.text}
                    </div>
                  </div>
                </motion.div>
              ))}
              
              {isTyping && (
                <div className="flex justify-start">
                  <div className="flex items-end gap-2">
                    <div className="w-6 h-6 rounded-full bg-red-500/20 border border-red-500/30 flex items-center justify-center text-red-500 shadow-[0_0_10px_rgba(239,68,68,0.3)]">
                      <SafeIcon icon={FiCpu} className="w-3 h-3" />
                    </div>
                    <TypingIndicator />
                  </div>
                </div>
              )}
            </div>

            {/* Quick Actions */}
            <div className="px-4 pb-2 flex gap-2 overflow-x-auto no-scrollbar">
              {quickActions.map((action, idx) => (
                <button
                  key={idx}
                  onClick={() => handleSend(action.action)}
                  className="whitespace-nowrap px-3 py-1.5 bg-white/5 hover:bg-red-500/20 border border-white/10 hover:border-red-500/50 rounded-full text-[10px] text-white/70 hover:text-white transition-all flex-shrink-0 shadow-sm hover:shadow-[0_0_10px_rgba(239,68,68,0.2)]"
                >
                  {action.label}
                </button>
              ))}
            </div>

            {/* Input Area */}
            <div className="p-3 bg-black border-t border-white/10">
              <div className="flex items-center gap-2 bg-zinc-900/50 border border-white/10 rounded-xl p-1 pl-3 focus-within:border-red-500/50 focus-within:shadow-[0_0_15px_rgba(239,68,68,0.15)] transition-all">
                <input
                  type="text"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleSend()}
                  placeholder="Ask a question..."
                  className="flex-1 bg-transparent text-xs text-white placeholder-white/30 focus:outline-none py-2"
                />
                <button 
                  onClick={() => handleSend()}
                  disabled={!input.trim()}
                  className="p-2 bg-red-600 text-white rounded-lg hover:bg-red-500 disabled:opacity-50 disabled:hover:bg-red-600 transition-all shadow-[0_0_15px_rgba(220,38,38,0.4)] hover:shadow-[0_0_20px_rgba(220,38,38,0.6)]"
                >
                  <SafeIcon icon={FiSend} className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <motion.button
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.9 }}
        onClick={() => setIsOpen(!isOpen)}
        className="w-14 h-14 bg-gradient-to-br from-red-600 to-red-800 text-white rounded-full flex items-center justify-center shadow-[0_0_25px_rgba(220,38,38,0.6)] hover:shadow-[0_0_40px_rgba(220,38,38,0.8)] transition-all border border-red-400/30 relative z-50 overflow-hidden group"
      >
        <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent"></div>
        <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-700 bg-[radial-gradient(circle_at_center,_rgba(255,255,255,0.4),transparent_70%)]"></div>
        <SafeIcon icon={isOpen ? FiX : FiMessageSquare} className="w-6 h-6 relative z-10 drop-shadow-md" />
        
        {!isOpen && (
          <span className="absolute top-0 right-0 w-4 h-4 bg-green-500 border-2 border-black rounded-full shadow-[0_0_10px_rgba(34,197,94,0.8)] animate-pulse"></span>
        )}
      </motion.button>
    </div>
  );
};

const SetupImage = ({ src }) => {
  const [loading, setLoading] = useState(true);
  
  return (
    <div className="relative w-full flex justify-center py-2">
      <motion.div 
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className={`max-w-[280px] md:max-w-[360px] w-full min-h-[120px] md:min-h-[160px] rounded-xl overflow-hidden border border-white/10 shadow-[0_0_30px_rgba(0,0,0,0.5)] flex justify-center bg-black/40 p-1 relative ${loading ? 'animate-pulse' : ''} hover:border-red-500/30 hover:shadow-[0_0_20px_rgba(239,68,68,0.1)] transition-all duration-500`}
      >
        {loading && (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-8 h-8 border-2 border-red-500/30 border-t-red-500 rounded-full animate-spin shadow-[0_0_15px_rgba(239,68,68,0.4)]"></div>
          </div>
        )}
        <img 
          src={src} 
          alt="Setup Reference" 
          className={`max-w-full h-full object-contain transition-opacity duration-500 ${loading ? 'opacity-0' : 'opacity-100'}`}
          onLoad={() => setLoading(false)}
        />
      </motion.div>
    </div>
  );
};

function App() {
  const [activeTab, setActiveTab] = useState('wizard'); // 'wizard' | 'calculator' | 'strike'
  const [currentStep, setCurrentStep] = useState(1);
  const [selectedStyle, setSelectedStyle] = useState('');
  const [selectedAction, setSelectedAction] = useState('');
  const [wizardStepIndex, setWizardStepIndex] = useState(-1);
  const [completed, setCompleted] = useState(false);
  const [failedRules, setFailedRules] = useState(false);
  const [failedStep, setFailedStep] = useState(null);
  const [isChatOpen, setIsChatOpen] = useState(false);

  useEffect(() => {
    const urlsToPreload = [LOGO_URL];
    Object.values(WIZARD_CONFIG).forEach(riskLevel => {
      Object.values(riskLevel).forEach(actionSteps => {
        actionSteps.forEach(step => {
          if (step.image) urlsToPreload.push(step.image);
        });
      });
    });
    urlsToPreload.forEach(url => {
      const img = new Image();
      img.src = url;
    });
  }, []);

  const lowRiskOptions = [
    { code: 'A', label: 'Angle', icon: FiActivity },
    { code: 'B', label: 'Breakout', icon: FiTarget },
    { code: 'C', label: 'Candles', icon: FiLayers },
    { code: 'M', label: 'Money Flow', icon: FiSettings },
    { code: 'P', label: 'Percentage', icon: FiBarChart2 }
  ];

  const highRiskOptions = [
    { code: 'M', label: 'Money Flow', icon: FiSettings },
    { code: 'A', label: 'Angle', icon: FiActivity },
    { code: 'C', label: 'Candles', icon: FiLayers },
    { code: 'P', label: 'Percentage', icon: FiBarChart2 }
  ];

  const handleGetStarted = () => setCurrentStep(2);
  const handleStyleSelect = (style) => {
    setSelectedStyle(style);
    setCurrentStep(3);
  };
  const handleConfirmStyle = () => setCurrentStep(4);
  const handleActionSelect = (action) => {
    setSelectedAction(action);
    setWizardStepIndex(0);
  };

  const currentStepsList = (selectedStyle && selectedAction) 
    ? WIZARD_CONFIG[selectedStyle][selectedAction] 
    : [];

  const handleTrueSelect = () => {
    if (wizardStepIndex < currentStepsList.length - 1) {
      setWizardStepIndex(prev => prev + 1);
    } else {
      setFailedRules(false);
      setFailedStep(null);
      setCompleted(true);
    }
  };

  const handleFalseSelect = () => {
    setFailedStep(currentStepsList[wizardStepIndex]);
    setFailedRules(true);
    setCompleted(true);
  };

  const goBackWizardStep = () => {
    if (wizardStepIndex > 0) {
      setWizardStepIndex(prev => prev - 1);
    } else {
      setWizardStepIndex(-1);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
      setCompleted(false);
    }
  };

  const handleReset = () => {
    setCurrentStep(1);
    setSelectedStyle('');
    setSelectedAction('');
    setWizardStepIndex(-1);
    setCompleted(false);
    setFailedRules(false);
    setFailedStep(null);
  };

  const isSubWizard = wizardStepIndex !== -1 && !completed;
  const currentStepData = isSubWizard ? currentStepsList[wizardStepIndex] : null;

  const getThemeColors = () => {
    if (selectedAction === 'Buying') return {
      bg: 'to-green-950/20',
      accent: 'text-green-500',
      btn: 'bg-green-600 hover:bg-green-700 shadow-[0_0_20px_rgba(22,163,74,0.4)]',
      border: 'border-green-500/30'
    };
    if (selectedAction === 'Selling') return {
      bg: 'to-red-950/20',
      accent: 'text-red-500',
      btn: 'bg-red-600 hover:bg-red-700 shadow-[0_0_20px_rgba(220,38,38,0.4)]',
      border: 'border-red-500/30'
    };
    return {
      bg: 'to-black',
      accent: 'text-red-500',
      btn: 'bg-red-600 hover:bg-red-700 shadow-[0_0_20px_rgba(220,38,38,0.4)]',
      border: 'border-red-500/30'
    };
  };

  const theme = getThemeColors();

  return (
    <div className={`min-h-[100dvh] bg-gradient-to-br from-black via-zinc-950 ${theme.bg} flex items-center justify-center p-4 transition-all duration-1000 overflow-x-hidden relative`}>
      
      {/* Absolute Header */}
      <div className="absolute top-4 left-1/2 -translate-x-1/2 w-full max-w-6xl px-4 flex justify-between items-center z-20">
        <motion.div 
          whileHover={{ scale: 1.05 }}
          className="flex items-center gap-4 group cursor-pointer" 
          onClick={handleReset}
        >
          <div className="w-16 h-16 md:w-20 md:h-20 bg-black/40 backdrop-blur-md rounded-2xl flex items-center justify-center shadow-[0_0_20px_rgba(220,38,38,0.2)] border border-white/10 group-hover:border-red-500/50 group-hover:shadow-[0_0_30px_rgba(220,38,38,0.4)] transition-all p-2">
            <img src={LOGO_URL} alt="Rocketalgo Logo" className="w-full h-full object-contain drop-shadow-[0_0_5px_rgba(255,255,255,0.5)]" />
          </div>
          <span className="text-xl md:text-2xl font-black text-white tracking-wider uppercase drop-shadow-[0_0_10px_rgba(255,255,255,0.3)] hidden sm:block">Rocketalgo Setup Guide</span>
        </motion.div>
        
        <div className="flex items-center gap-3 md:gap-6">
          <div className="flex items-center gap-4">
            <a 
              href="https://rocketalgo.in/" 
              target="_blank" 
              rel="noopener noreferrer"
              className="hidden md:flex items-center gap-2 text-[11px] font-black text-white bg-white/5 hover:bg-white/10 px-4 py-2 rounded-lg uppercase tracking-widest transition-all border border-white/10 hover:border-red-500/30 hover:shadow-[0_0_15px_rgba(220,38,38,0.2)]"
            >
              Rocketalgo Official Site
              <SafeIcon icon={FiExternalLink} className="w-3 h-3" />
            </a>
            <motion.a 
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              href="https://pages.razorpay.com/rcktalgonewplan" 
              target="_blank" 
              rel="noopener noreferrer"
              className="bg-red-600 hover:bg-red-500 text-white text-[10px] font-black uppercase tracking-widest px-4 py-2 rounded-lg transition-all shadow-[0_0_20px_rgba(220,38,38,0.5)] hover:shadow-[0_0_30px_rgba(220,38,38,0.7)] flex items-center gap-2 border border-red-500/50"
            >
              <span>Subscribe Now</span>
              <SafeIcon icon={FiZap} className="w-3 h-3 animate-pulse" />
            </motion.a>
          </div>

          {!completed && currentStep > 1 && (
            <div className="hidden lg:flex items-center gap-3 text-white text-[10px] font-bold uppercase tracking-widest bg-black/40 px-4 py-2 rounded-full border border-white/10 backdrop-blur-sm shadow-[0_0_10px_rgba(0,0,0,0.5)]">
              <span>Setup Wizard</span>
              <div className="w-1 h-1 bg-red-500 rounded-full shadow-[0_0_5px_rgba(239,68,68,1)]"></div>
              <span className="text-red-500 text-shadow-red">V2.4.0</span>
            </div>
          )}
        </div>
      </div>

      <div className="w-full max-w-5xl mx-auto pt-28 pb-32 flex flex-col items-center relative z-10">
        
        {/* Tab Navigation */}
        <motion.div 
          initial={{ y: -20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.2 }}
          className="mb-8 bg-black/60 backdrop-blur-xl p-1.5 rounded-2xl border border-white/10 inline-flex shadow-[0_0_30px_rgba(0,0,0,0.5)] relative z-30 flex-wrap justify-center gap-2"
        >
          {[
            { id: 'wizard', icon: FiGrid, label: 'Setup Guide' },
            { id: 'calculator', icon: FiCalculator, label: 'StopLoss Calc' },
            { id: 'strike', icon: FiTarget, label: 'Strike Price' }
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 md:px-6 py-2.5 rounded-xl text-[10px] md:text-xs font-black uppercase tracking-widest transition-all duration-300 flex items-center gap-2 relative overflow-hidden ${
                activeTab === tab.id 
                  ? 'bg-red-600 text-white shadow-[0_0_20px_rgba(220,38,38,0.5)] border border-red-400/30' 
                  : 'text-white/50 hover:text-white hover:bg-white/5 hover:border-white/10 border border-transparent'
              }`}
            >
              <SafeIcon icon={tab.icon} className={`w-3.5 h-3.5 ${activeTab === tab.id ? 'animate-pulse' : ''}`} />
              {tab.label}
              {activeTab === tab.id && (
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent translate-x-[-100%] animate-[shimmer_2s_infinite]"></div>
              )}
            </button>
          ))}
        </motion.div>

        <div className="w-full">
          <AnimatePresence mode="wait">
            {activeTab === 'calculator' ? (
              <motion.div key="calc" initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }}>
                <StopLossCalculator />
              </motion.div>
            ) : activeTab === 'strike' ? (
              <motion.div key="strike" initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }}>
                <StrikePriceCalculator />
              </motion.div>
            ) : (
              <motion.div key="wizard-container" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="w-full">
                <AnimatePresence>
                  {((currentStep > 1 && !isSubWizard) || (isSubWizard)) && !completed && (
                    <motion.div
                      key="back-button"
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -10 }}
                      className="mb-4 pl-4"
                    >
                      <button
                        onClick={isSubWizard ? goBackWizardStep : handleBack}
                        className={`flex items-center gap-2 text-white hover:text-red-400 bg-black/40 border border-white/10 hover:border-red-500/30 px-4 py-2 rounded-xl transition-all duration-300 group touch-manipulation backdrop-blur-sm shadow-lg hover:shadow-[0_0_15px_rgba(220,38,38,0.2)]`}
                      >
                        <SafeIcon icon={FiArrowLeft} className="w-3.5 h-3.5 group-hover:-translate-x-1 transition-transform" />
                        <span className="font-bold text-[10px] uppercase tracking-widest">Back</span>
                      </button>
                    </motion.div>
                  )}
                </AnimatePresence>

                <AnimatePresence mode="wait">
                  {currentStep === 1 && (
                    <motion.div
                      key="step1"
                      initial={{ opacity: 0, scale: 0.95, y: 20 }}
                      animate={{ opacity: 1, scale: 1, y: 0 }}
                      exit={{ opacity: 0, scale: 0.95, y: -20 }}
                      className="flex flex-col items-center justify-center space-y-8 pt-4"
                    >
                      {/* Landing Page Content */}
                      <div className="text-center space-y-6 px-4">
                        <motion.div 
                          initial={{ y: 20, opacity: 0 }}
                          animate={{ y: 0, opacity: 1 }}
                          transition={{ delay: 0.1 }}
                          className="flex justify-center mb-4"
                        >
                          <div className="w-48 h-48 md:w-64 md:h-64 bg-black/40 rounded-[2.5rem] p-8 shadow-[0_0_50px_rgba(220,38,38,0.15)] border border-white/10 backdrop-blur-xl hover:shadow-[0_0_70px_rgba(220,38,38,0.25)] transition-all duration-500">
                            <img src={LOGO_URL} alt="Rocketalgo Logo Large" className="w-full h-full object-contain drop-shadow-[0_0_15px_rgba(255,255,255,0.2)]" />
                          </div>
                        </motion.div>
                        
                        <div className="space-y-4">
                          <motion.div 
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ delay: 0.3 }}
                            className="inline-block px-4 py-1.5 bg-red-500/10 border border-red-500/30 rounded-full text-red-500 text-[10px] font-black uppercase tracking-[0.2em] mb-2 shadow-[0_0_15px_rgba(220,38,38,0.2)]"
                          >
                            Trading Excellence
                          </motion.div>
                          <motion.h1 
                            initial={{ y: 20, opacity: 0 }}
                            animate={{ y: 0, opacity: 1 }}
                            transition={{ delay: 0.4 }}
                            className="text-4xl md:text-7xl font-black text-white tracking-tighter leading-[0.9] drop-shadow-2xl"
                          >
                            OBSERVE<br /><span className="text-red-600 glow-text-red">ROCKETALGO</span><br />SETUP
                          </motion.h1>
                        </div>
                        
                        <motion.p 
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          transition={{ delay: 0.5 }}
                          className="text-base md:text-xl text-white/70 font-medium tracking-tight max-w-xl mx-auto leading-relaxed"
                        >
                          Precision trading guided by advanced algorithmic rules. Validate your next move with confidence.
                        </motion.p>
                      </div>
                      <motion.button
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.6 }}
                        whileHover={{ scale: 1.05, y: -2, boxShadow: "0 0 40px rgba(220,38,38,0.6)" }}
                        whileTap={{ scale: 0.95 }}
                        onClick={handleGetStarted}
                        className="bg-red-600 hover:bg-red-500 text-white px-12 py-5 text-lg font-black rounded-2xl shadow-[0_0_30px_rgba(220,38,38,0.4)] transition-all duration-300 touch-manipulation uppercase tracking-[0.2em] border border-red-400/30 relative overflow-hidden"
                      >
                        <span className="relative z-10">Launch Guide</span>
                        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent translate-x-[-100%] animate-[shimmer_3s_infinite]"></div>
                      </motion.button>
                    </motion.div>
                  )}

                  {currentStep === 2 && (
                    <motion.div
                      key="step2"
                      initial={{ opacity: 0, x: 30 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -30 }}
                      className="px-2"
                    >
                      <div className="text-center mb-8 md:mb-12">
                        <h2 className="text-3xl md:text-4xl font-black text-white mb-2 tracking-tighter drop-shadow-lg">TRADING STYLE</h2>
                        <p className="text-white/60 text-sm md:text-base font-medium">Select your preferred risk management profile</p>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-8 max-w-4xl mx-auto">
                        <motion.button
                          whileHover={{ scale: 1.02, y: -5, borderColor: "rgba(34,197,94,0.5)", boxShadow: "0 0 30px rgba(34,197,94,0.2)" }}
                          whileTap={{ scale: 0.98 }}
                          onClick={() => handleStyleSelect('Low Risk')}
                          className="relative overflow-hidden bg-black/40 border-2 border-white/10 p-8 md:p-10 rounded-[2.5rem] flex flex-col items-center gap-6 group transition-all shadow-xl backdrop-blur-sm"
                        >
                          <div className="absolute top-0 right-0 p-4 opacity-5 group-hover:opacity-10 transition-opacity">
                            <SafeIcon icon={FiShield} className="w-20 h-20" />
                          </div>
                          <div className="w-16 h-16 bg-green-500/10 rounded-2xl flex items-center justify-center text-green-500 group-hover:bg-green-500 group-hover:text-white transition-all duration-500 shadow-[0_0_15px_rgba(34,197,94,0.1)] group-hover:shadow-[0_0_25px_rgba(34,197,94,0.4)]">
                            <SafeIcon icon={FiShield} className="w-8 h-8" />
                          </div>
                          <div className="text-center relative z-10">
                            <span className="text-2xl md:text-3xl font-black text-white block mb-1 tracking-tighter group-hover:text-green-400 transition-colors">LOW RISK</span>
                            <span className="text-green-500 text-[10px] font-black uppercase tracking-[0.15em] bg-green-500/10 px-3 py-0.5 rounded-full border border-green-500/20">Conservative</span>
                          </div>
                          <div className="w-full h-1 bg-white/5 rounded-full overflow-hidden">
                            <div className="w-1/3 h-full bg-green-500 shadow-[0_0_10px_rgba(34,197,94,0.8)]"></div>
                          </div>
                        </motion.button>

                        <motion.button
                          whileHover={{ scale: 1.02, y: -5, borderColor: "rgba(239,68,68,0.5)", boxShadow: "0 0 30px rgba(239,68,68,0.2)" }}
                          whileTap={{ scale: 0.98 }}
                          onClick={() => handleStyleSelect('High Risk')}
                          className="relative overflow-hidden bg-black/40 border-2 border-white/10 p-8 md:p-10 rounded-[2.5rem] flex flex-col items-center gap-6 group transition-all shadow-xl backdrop-blur-sm"
                        >
                          <div className="absolute top-0 right-0 p-4 opacity-5 group-hover:opacity-10 transition-opacity">
                            <SafeIcon icon={FiTrendingUp} className="w-20 h-20" />
                          </div>
                          <div className="w-16 h-16 bg-red-500/10 rounded-2xl flex items-center justify-center text-red-500 group-hover:bg-red-500 group-hover:text-white transition-all duration-500 shadow-[0_0_15px_rgba(239,68,68,0.1)] group-hover:shadow-[0_0_25px_rgba(239,68,68,0.4)]">
                            <SafeIcon icon={FiTrendingUp} className="w-8 h-8" />
                          </div>
                          <div className="text-center relative z-10">
                            <span className="text-2xl md:text-3xl font-black text-white block mb-1 tracking-tighter group-hover:text-red-400 transition-colors">HIGH RISK</span>
                            <span className="text-red-500 text-[10px] font-black uppercase tracking-[0.15em] bg-red-500/10 px-3 py-0.5 rounded-full border border-red-500/20">Aggressive</span>
                          </div>
                          <div className="w-full h-1 bg-white/5 rounded-full overflow-hidden">
                            <div className="w-full h-full bg-red-500 shadow-[0_0_10px_rgba(239,68,68,0.8)]"></div>
                          </div>
                        </motion.button>
                      </div>
                    </motion.div>
                  )}

                  {currentStep === 3 && (
                    <motion.div
                      key="step3"
                      initial={{ opacity: 0, scale: 0.98 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 0.98 }}
                      className="max-w-4xl mx-auto px-4"
                    >
                      <div className="bg-black/40 backdrop-blur-xl border-2 border-white/10 rounded-[2.5rem] p-6 md:p-10 text-center shadow-[0_0_50px_rgba(0,0,0,0.5)] relative overflow-hidden">
                        <div className={`absolute -top-16 -left-16 w-48 h-48 ${selectedStyle === 'Low Risk' ? 'bg-green-500/10' : 'bg-red-500/10'} blur-[80px] rounded-full`}></div>
                        
                        <div className="flex justify-center mb-4 relative">
                          <div className={`w-14 h-14 ${selectedStyle === 'Low Risk' ? 'bg-green-500/20 text-green-500 border-green-500/30' : 'bg-red-500/20 text-red-500 border-red-500/30'} rounded-2xl flex items-center justify-center shadow-lg border backdrop-blur-sm`}>
                            <SafeIcon icon={selectedStyle === 'Low Risk' ? FiShield : FiTrendingUp} className="w-7 h-7" />
                          </div>
                        </div>

                        <h2 className="text-3xl md:text-5xl font-black text-white mb-2 tracking-tighter uppercase relative drop-shadow-lg">
                          {selectedStyle} Setup
                        </h2>
                        <p className="text-white/60 text-sm md:text-base font-medium mb-8 tracking-tight">
                          The following <span className="text-red-500 font-bold glow-text-red">Rocketalgo</span> pointers must be validated:
                        </p>
                        
                        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3 mb-8 relative">
                          {(selectedStyle === 'Low Risk' ? lowRiskOptions : highRiskOptions).map((opt, idx) => (
                            <motion.div 
                              key={opt.code}
                              initial={{ opacity: 0, y: 10 }}
                              animate={{ opacity: 1, y: 0 }}
                              transition={{ delay: idx * 0.05 }}
                              whileHover={{ y: -5, borderColor: "rgba(239,68,68,0.4)" }}
                              className="bg-black/40 border border-white/5 p-5 md:p-6 rounded-2xl flex flex-col items-center justify-center gap-3 group transition-all shadow-lg backdrop-blur-sm"
                            >
                              <div className="w-10 h-10 bg-white/5 rounded-xl flex items-center justify-center text-white/60 group-hover:text-red-500 transition-colors group-hover:bg-red-500/10 group-hover:shadow-[0_0_15px_rgba(239,68,68,0.2)]">
                                <SafeIcon icon={opt.icon} className="w-5 h-5" />
                              </div>
                              <div className="text-center">
                                <span className="text-red-600 font-black text-3xl md:text-4xl block leading-none mb-1 drop-shadow-[0_0_15px_rgba(220,38,38,0.4)] group-hover:scale-105 transition-transform">
                                  {opt.code}
                                </span>
                                <span className="text-white/80 font-black text-[8px] md:text-[10px] uppercase tracking-[0.15em]">
                                  {opt.label}
                                </span>
                              </div>
                            </motion.div>
                          ))}
                        </div>

                        <motion.button
                          whileHover={{ scale: 1.02, y: -2, boxShadow: "0 0 30px rgba(220,38,38,0.5)" }}
                          whileTap={{ scale: 0.98 }}
                          onClick={handleConfirmStyle}
                          className="w-full bg-red-600 hover:bg-red-500 text-white py-4 md:py-6 rounded-2xl font-black text-lg md:text-xl shadow-[0_0_20px_rgba(220,38,38,0.3)] uppercase tracking-[0.2em] transition-all relative z-10 border border-red-500/30 overflow-hidden"
                        >
                          <span className="relative z-10">Let's Go</span>
                          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent translate-x-[-100%] animate-[shimmer_2s_infinite]"></div>
                        </motion.button>
                      </div>
                    </motion.div>
                  )}

                  {currentStep === 4 && !isSubWizard && !completed && (
                    <motion.div
                      key="step4"
                      initial={{ opacity: 0, x: 30 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -30 }}
                      className="px-4"
                    >
                      <div className="text-center mb-8 md:mb-12">
                        <h2 className="text-3xl md:text-4xl font-black text-white mb-2 tracking-tighter drop-shadow-lg">MARKET ACTION</h2>
                        <p className="text-white/60 text-sm md:text-base font-medium">Strategy: <span className="text-red-500 font-black uppercase text-shadow-red">{selectedStyle}</span></p>
                      </div>
                      <div className="flex flex-col md:flex-row gap-4 md:gap-8 justify-center items-center max-w-3xl mx-auto">
                        <motion.button
                          whileHover={{ scale: 1.05, y: -5, boxShadow: "0 0 40px rgba(22,163,74,0.3)" }}
                          whileTap={{ scale: 0.95 }}
                          onClick={() => handleActionSelect('Buying')}
                          className="w-full md:w-72 h-36 md:h-48 bg-gradient-to-br from-green-700 to-green-900 border border-green-500/30 text-white text-2xl font-black rounded-[2.5rem] shadow-[0_0_20px_rgba(22,163,74,0.2)] touch-manipulation uppercase tracking-wider flex flex-col items-center justify-center gap-3 relative overflow-hidden group"
                        >
                          <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,_rgba(34,197,94,0.3),transparent_70%)] opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                          <SafeIcon icon={FiArrowLeft} className="w-8 h-8 rotate-90 relative z-10" />
                          <span className="relative z-10 text-shadow-sm">Buying</span>
                        </motion.button>
                        <motion.button
                          whileHover={{ scale: 1.05, y: -5, boxShadow: "0 0 40px rgba(220,38,38,0.3)" }}
                          whileTap={{ scale: 0.95 }}
                          onClick={() => handleActionSelect('Selling')}
                          className="w-full md:w-72 h-36 md:h-48 bg-gradient-to-br from-red-700 to-red-900 border border-red-500/30 text-white text-2xl font-black rounded-[2.5rem] shadow-[0_0_20px_rgba(220,38,38,0.2)] touch-manipulation uppercase tracking-wider flex flex-col items-center justify-center gap-3 relative overflow-hidden group"
                        >
                          <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,_rgba(239,68,68,0.3),transparent_70%)] opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                          <SafeIcon icon={FiArrowLeft} className="w-8 h-8 -rotate-90 relative z-10" />
                          <span className="relative z-10 text-shadow-sm">Selling</span>
                        </motion.button>
                      </div>
                    </motion.div>
                  )}

                  {isSubWizard && currentStepData && (
                    <motion.div
                      key={`${selectedStyle}-${selectedAction}-${currentStepData.id}`}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -20 }}
                      className="max-w-3xl mx-auto px-2"
                    >
                      <div className={`bg-black/60 backdrop-blur-xl border-2 ${theme.border} rounded-[2.5rem] p-6 md:p-10 shadow-[0_0_40px_rgba(0,0,0,0.5)] relative overflow-hidden`}>
                        <div className="absolute top-0 left-0 w-full h-1.5 bg-white/5">
                          <motion.div 
                            initial={{ width: 0 }}
                            animate={{ width: `${((wizardStepIndex + 1) / currentStepsList.length) * 100}%` }}
                            className={`h-full ${selectedAction === 'Buying' ? 'bg-green-500 shadow-[0_0_10px_rgba(34,197,94,0.8)]' : 'bg-red-500 shadow-[0_0_10px_rgba(239,68,68,0.8)]'}`}
                          />
                        </div>

                        <div className="flex flex-col md:flex-row items-center md:items-start gap-6 mb-8">
                          <div className={`w-16 h-16 md:w-20 md:h-20 bg-black/40 rounded-2xl flex items-center justify-center ${theme.accent} border border-white/10 shadow-lg backdrop-blur-sm`}>
                            <SafeIcon icon={currentStepData.icon} className="w-8 h-8 md:w-10 md:h-10 drop-shadow-md" />
                          </div>
                          <div className="text-center md:text-left pt-1">
                            <h2 className="text-2xl md:text-3xl font-black text-white tracking-tighter mb-1.5 drop-shadow-lg">{currentStepData.title}</h2>
                            <div className="flex flex-wrap justify-center md:justify-start gap-2">
                              <span className={`${theme.accent} text-[9px] font-black tracking-[0.15em] uppercase bg-white/5 px-2.5 py-0.5 rounded-full border border-white/5 shadow-sm`}>{selectedStyle}</span>
                              <span className={`${theme.accent} text-[9px] font-black tracking-[0.15em] uppercase bg-white/5 px-2.5 py-0.5 rounded-full border border-white/5 shadow-sm`}>{selectedAction}</span>
                              <span className="text-white/60 text-[9px] font-black tracking-[0.15em] uppercase bg-white/5 px-2.5 py-0.5 rounded-full border border-white/5">Rule {wizardStepIndex + 1}/{currentStepsList.length}</span>
                            </div>
                          </div>
                        </div>

                        <div className="space-y-6">
                          {currentStepData.image && (
                            <SetupImage src={currentStepData.image} />
                          )}

                          <div className="bg-black/30 border border-white/10 rounded-2xl p-6 md:p-10 text-center min-h-[140px] flex flex-col justify-center shadow-inner relative group backdrop-blur-sm hover:border-white/20 transition-colors">
                            <div className="absolute top-3 right-3 text-white/20 group-hover:text-red-500/50 transition-colors">
                              <SafeIcon icon={FiInfo} className="w-6 h-6" />
                            </div>
                            <h3 className="text-xl md:text-2xl text-white font-bold mb-4 leading-snug tracking-tight drop-shadow-md">
                              {currentStepData.question}
                            </h3>
                            <p className="text-white/60 text-[11px] md:text-xs font-medium italic max-w-xl mx-auto leading-relaxed">
                              {currentStepData.detail}
                            </p>
                          </div>

                          <div className="grid grid-cols-2 gap-4">
                            <motion.button
                              whileHover={{ scale: 1.02, y: -3, boxShadow: "0 0 25px rgba(22,163,74,0.4)" }}
                              whileTap={{ scale: 0.98 }}
                              onClick={handleTrueSelect}
                              className="bg-green-600 hover:bg-green-500 text-white font-black py-4 md:py-6 rounded-xl shadow-[0_0_15px_rgba(22,163,74,0.2)] transition-all flex items-center justify-center gap-3 touch-manipulation text-base uppercase tracking-widest border border-green-400/30"
                            >
                              <SafeIcon icon={FiCheck} className="w-5 h-5" strokeWidth={3} />
                              True
                            </motion.button>
                            <motion.button
                              whileHover={{ scale: 1.02, y: -3, boxShadow: "0 0 25px rgba(220,38,38,0.4)" }}
                              whileTap={{ scale: 0.98 }}
                              onClick={handleFalseSelect}
                              className="bg-red-600 hover:bg-red-500 text-white font-black py-4 md:py-6 rounded-xl shadow-[0_0_15px_rgba(220,38,38,0.2)] transition-all flex items-center justify-center gap-3 touch-manipulation text-base uppercase tracking-widest border border-red-400/30"
                            >
                              <SafeIcon icon={FiX} className="w-5 h-5" strokeWidth={3} />
                              False
                            </motion.button>
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  )}

                  {completed && (
                    <motion.div
                      key="final"
                      initial={{ opacity: 0, scale: 0.98 }}
                      animate={{ opacity: 1, scale: 1 }}
                      className="flex flex-col items-center justify-center space-y-8 text-center px-4 py-8"
                    >
                      <div className="relative">
                        <motion.div
                          initial={{ scale: 0, rotate: -45 }}
                          animate={{ scale: 1, rotate: 0 }}
                          transition={{ type: "spring", stiffness: 200, delay: 0.1 }}
                          className={`w-24 h-24 md:w-32 md:h-32 rounded-[2.5rem] flex items-center justify-center shadow-2xl relative z-10 ${failedRules ? 'bg-red-600 shadow-[0_0_60px_rgba(220,38,38,0.5)] border border-red-400/50' : 'bg-green-600 shadow-[0_0_60px_rgba(22,163,74,0.5)] border border-green-400/50'}`}
                        >
                          <SafeIcon icon={failedRules ? FiX : FiCheck} className="w-10 h-10 md:w-14 md:h-14 text-white drop-shadow-md" strokeWidth={4} />
                        </motion.div>
                        <div className={`absolute inset-0 blur-3xl opacity-30 ${failedRules ? 'bg-red-500' : 'bg-green-500'} animate-pulse`}></div>
                      </div>
                      
                      <div className="space-y-4 max-w-2xl px-2">
                        <h2 className="text-4xl md:text-6xl font-black text-white mb-4 tracking-tighter uppercase leading-none drop-shadow-2xl">
                          {failedRules ? <><span className="text-red-500 glow-text-red">SETUP</span><br />FAILED</> : <><span className="text-green-500 glow-text-green">SETUP</span><br />COMPLETE</>}
                        </h2>
                        
                        {failedRules ? (
                          <div className="bg-red-900/10 border border-red-500/30 p-6 rounded-2xl shadow-[0_0_30px_rgba(220,38,38,0.1)] backdrop-blur-sm">
                            <p className="text-lg md:text-xl text-red-500 font-black leading-tight uppercase tracking-tight mb-2 text-shadow-red">
                              CRITICAL: Rule Violation detected.
                            </p>
                            {failedStep && (
                              <div className="flex items-center justify-center gap-2 bg-red-500/10 py-3 px-5 rounded-xl border border-red-500/20 mt-4">
                                <SafeIcon icon={failedStep.icon} className="w-4 h-4 text-red-500" />
                                <span className="text-white/90 font-black text-xs uppercase tracking-widest">Failed at {failedStep.title}</span>
                              </div>
                            )}
                          </div>
                        ) : (
                          <div className="bg-green-900/10 border border-green-500/30 p-6 rounded-2xl shadow-[0_0_30px_rgba(22,163,74,0.1)] backdrop-blur-sm">
                            <p className="text-lg md:text-xl text-white font-bold leading-tight tracking-tight">
                              Your <span className={theme.accent + " font-black uppercase text-shadow-sm"}>{selectedStyle}</span> setup for <span className={theme.accent + " font-black uppercase text-shadow-sm"}>{selectedAction}</span> is valid.
                            </p>
                          </div>
                        )}

                        <div className="mt-8 p-8 bg-black/40 backdrop-blur-xl rounded-[2.5rem] border border-white/10 inline-block text-left w-full max-w-md shadow-2xl">
                          <div className="space-y-4">
                            <div className="flex justify-between items-center border-b border-white/10 pb-3">
                              <span className="text-white/50 font-black uppercase tracking-widest text-[10px]">Strategy</span>
                              <span className="text-white font-black text-lg tracking-tighter">{selectedStyle}</span>
                            </div>
                            <div className="flex justify-between items-center border-b border-white/10 pb-3">
                              <span className="text-white/50 font-black uppercase tracking-widest text-[10px]">Action</span>
                              <span className={`${theme.accent} font-black text-lg tracking-tighter drop-shadow-sm`}>{selectedAction}</span>
                            </div>
                            <div className="flex justify-between items-center">
                              <span className="text-white/50 font-black uppercase tracking-widest text-[10px]">Final Verdict</span>
                              <span className={failedRules ? 'text-red-500 font-black text-lg tracking-tighter glow-text-red' : 'text-green-500 font-black text-lg tracking-tighter glow-text-green'}>
                                {failedRules ? 'NO TRADE' : 'VALID ENTRY'}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>

                      <motion.button
                        whileHover={{ scale: 1.05, boxShadow: "0 0 30px rgba(255,255,255,0.2)" }}
                        whileTap={{ scale: 0.95 }}
                        onClick={handleReset}
                        className="bg-white hover:bg-zinc-100 text-black px-12 py-5 rounded-2xl font-black text-base uppercase tracking-[0.15em] transition-all touch-manipulation shadow-xl"
                      >
                        Start New Observer
                      </motion.button>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>

      <Chatbot 
        currentStepData={currentStepData} 
        currentStep={currentStep}
        isOpen={isChatOpen} 
        setIsOpen={setIsChatOpen} 
      />

      {/* Footer Branding */}
      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 text-center w-full px-4 z-50">
        <div className="text-white/30 text-[10px] font-black uppercase tracking-[0.3em] mb-3">
          © Rocketalgo Analytics 2026 · All Rights Reserved
        </div>
        
        <div className="flex items-center justify-center group">
          <motion.div 
            whileHover={{ scale: 1.02 }}
            className="bg-black/60 backdrop-blur-md border border-white/10 hover:border-red-500/30 px-6 py-2 rounded-full flex items-center gap-3 shadow-[0_0_20px_rgba(0,0,0,0.5)] hover:shadow-[0_0_20px_rgba(220,38,38,0.2)] transition-all"
          >
            <span className="text-white/60 text-[10px] font-black uppercase tracking-widest">Contact Us to Join:</span>
            <a 
              href="tel:+917796983999" 
              className="text-red-500 text-sm md:text-base font-black tracking-widest transition-colors glow-text-red"
            >
              +91-77-969-83999
            </a>
          </motion.div>
        </div>
      </div>
    </div>
  );
}

export default App;